import { Controller } from "@hotwired/stimulus"
import {
  createEditor,
  $getRoot,
  $getSelection,
  $isRangeSelection,
  $isTextNode,
  $setSelection,
  $createParagraphNode,
  FORMAT_TEXT_COMMAND,
  ParagraphNode,
  TextNode
} from "lexical"
import { HeadingNode, registerRichText } from "@lexical/rich-text"
import {
  ListNode,
  ListItemNode,
  $insertList,
  $removeList,
  $isListNode,
  registerList
} from "@lexical/list"
import { LinkNode, $toggleLink } from "@lexical/link"
import { $generateHtmlFromNodes, $generateNodesFromDOM } from "@lexical/html"
import { $patchStyleText } from "@lexical/selection"
import { mergeRegister } from "@lexical/utils"

// Lexical rich-text editor replacing Trix on the feature editor surfaces.
//
// Boilerplate follows the canonical Lexical Quick Start
// (https://lexical.dev/docs/getting-started/quick-start):
//   1. createEditor with the node list and an error logger
//   2. setRootElement on the explicit contenteditable host
//   3. mergeRegister registerRichText + registerList + our output sync
//   4. editor.update to seed an empty ParagraphNode so the caret has a
//      block to live in (without this, typing into a fresh editor is
//      a no-op — the previous QA report)
//
// Other Stimulus controllers (ai-suggest, feature-translate,
// feature-editor-modal) drop generated HTML in by resolving this
// controller via document.querySelector(`[data-rich-editor-for=…]`)
// and calling .load(html).
export default class extends Controller {
  static targets = [
    "surface",
    "input",
    "btnBold",
    "btnItalic",
    "btnUnderline",
    "btnUl",
    "btnOl",
    "btnLink",
    "btnUnlink",
    "linkPanel",
    "linkInput",
    "linkError"
  ]
  static values = {
    inputId: String,
    disabled: { type: Boolean, default: false },
    namespace: { type: String, default: "feature_description" }
  }

  connect() {
    if (!this.hasSurfaceTarget) return

    this.editor = createEditor({
      namespace: `${this.namespaceValue}-${this._inputId() || "anon"}`,
      nodes: [ParagraphNode, TextNode, HeadingNode, ListNode, ListItemNode, LinkNode],
      theme: {
        text: {
          bold: "rich-editor__text--bold",
          italic: "rich-editor__text--italic",
          underline: "rich-editor__text--underline"
        }
      },
      onError: (err) => console.error("[lexical-editor]", err)
    })

    this.editor.setRootElement(this.surfaceTarget)
    this.editor.setEditable(!this.disabledValue)
    this.surfaceTarget.contentEditable = this.disabledValue ? "false" : "true"

    this._unregister = mergeRegister(
      registerRichText(this.editor),
      registerList(this.editor),
      this._registerOutputSync(),
      this._registerSelectionTracker()
    )

    // Seed initial state — either from the hidden input's pre-existing
    // HTML or with a blank paragraph so the caret can land.
    this.load(this._inputEl()?.value || "")
  }

  disconnect() {
    if (this._unregister) this._unregister()
    if (this.editor) this.editor.setRootElement(null)
    this._linkSelection = null
  }

  load(html) {
    if (!this.editor) return
    this.editor.update(() => {
      const root = $getRoot()
      root.clear()
      if (!html || !html.trim()) {
        root.append($createParagraphNode())
        return
      }
      const parser = new DOMParser()
      const dom = parser.parseFromString(html, "text/html")
      const nodes = $generateNodesFromDOM(this.editor, dom)
      // Lexical requires the root's direct children to be block-level.
      // $generateNodesFromDOM may return TextNodes at the top level for
      // bare text — wrap them in a paragraph so the editor stays valid.
      const wrapped = []
      let buffer = []
      const flush = () => {
        if (buffer.length === 0) return
        const p = $createParagraphNode()
        p.append(...buffer)
        wrapped.push(p)
        buffer = []
      }
      for (const node of nodes) {
        const type = node.getType?.()
        if (type === "paragraph" || type === "heading" || type === "list") {
          flush()
          wrapped.push(node)
        } else {
          buffer.push(node)
        }
      }
      flush()
      if (wrapped.length === 0) wrapped.push($createParagraphNode())
      root.append(...wrapped)

      // Lexical's default <span> import translates style → format flags
      // (bold/italic/etc.) but DROPS inline `color`. So a feature saved
      // with red text would render correctly on the public page (raw
      // HTML) but lose its color when the editor re-imported the HTML
      // on edit. Walk the source DOM + the resulting tree in tandem
      // and call setStyle on each TextNode whose source text was
      // inside a span with `style="color: …"`.
      this._reapplyInlineColors(dom)
    })
  }

  _reapplyInlineColors(dom) {
    const lexicalTexts = []
    const collect = (node) => {
      if ($isTextNode(node)) {
        if ((node.getTextContent() || "").length > 0) lexicalTexts.push(node)
        return
      }
      const children = node.getChildren?.() || []
      children.forEach(collect)
    }
    collect($getRoot())

    const domTexts = []
    const walker = dom.createTreeWalker(dom.body, NodeFilter.SHOW_TEXT)
    let n = walker.nextNode()
    while (n) {
      if (n.nodeValue && n.nodeValue.length > 0) domTexts.push(n)
      n = walker.nextNode()
    }

    const max = Math.min(lexicalTexts.length, domTexts.length)
    for (let i = 0; i < max; i++) {
      let parent = domTexts[i].parentNode
      let color = null
      while (parent && parent !== dom.body) {
        const inline = parent.style && parent.style.color
        if (inline) { color = inline; break }
        parent = parent.parentNode
      }
      if (color) lexicalTexts[i].setStyle("color: " + color)
    }
  }

  html() {
    if (!this.editor) return this._inputEl()?.value || ""

    let html = ""
    this.editor.getEditorState().read(() => {
      html = $generateHtmlFromNodes(this.editor, null)
    })
    return html
  }

  // ─── Toolbar actions ──────────────────────────────────────────────

  format(event) {
    event?.preventDefault?.()
    const which = event?.params?.format || event?.currentTarget?.dataset?.lexicalEditorFormatParam
    if (!which) return

    this.editor.update(() => {
      this._restoreLinkSelection()
      this.editor.dispatchCommand(FORMAT_TEXT_COMMAND, which)
    })
  }

  insertUnorderedList(event) {
    event?.preventDefault?.()
    this._toggleList("bullet")
  }

  insertOrderedList(event) {
    event?.preventDefault?.()
    this._toggleList("number")
  }

  _toggleList(type) {
    if (!this.editor) return

    this.editor.update(() => {
      this._restoreLinkSelection()
      if (this._selectionListType() === type) {
        $removeList()
      } else {
        $insertList(type)
      }
    })
  }

  _selectionListType() {
    const sel = $getSelection()
    if (!$isRangeSelection(sel)) return null

    let node = sel.anchor.getNode()
    while (node && node.getKey?.() !== "root") {
      if ($isListNode(node)) {
        return node.getListType() === "bullet" ? "bullet" : "number"
      }
      node = node.getParent?.()
    }

    return null
  }

  applyColor(event) {
    event?.preventDefault?.()
    const tgt = event?.currentTarget
    const raw =
      event?.params?.color ||
      tgt?.dataset?.lexicalEditorColorParam ||
      tgt?.value ||
      ""
    const color = raw === "none" || raw === "inherit" ? null : raw
    this.editor.update(() => {
      this._restoreLinkSelection()
      const sel = $getSelection()
      if (!$isRangeSelection(sel)) return
      $patchStyleText(sel, { color })
    })
  }

  preserveSelection(event) {
    event?.preventDefault?.()
    this._captureLinkSelection()
  }

  openLinkPanel(event) {
    event?.preventDefault?.()
    if (!this.hasLinkPanelTarget || !this.hasLinkInputTarget) return

    if (!this.linkPanelTarget.hidden) {
      this._closeLinkPanel()
      return
    }

    this._captureLinkSelection()
    this._clearLinkError()
    this.linkInputTarget.value = this._activeFormats?.linkUrl || ""
    this.linkPanelTarget.hidden = false
    this._syncLinkButtonState()

    window.requestAnimationFrame(() => {
      this.linkInputTarget.focus()
      this.linkInputTarget.select()
    })
  }

  applyLink(event) {
    event?.preventDefault?.()
    if (!this.hasLinkInputTarget) return

    const { url, error } = this._normalizeLinkUrl(this.linkInputTarget.value)
    if (error) {
      this._showLinkError(error)
      return
    }

    this.editor.update(() => {
      this._restoreLinkSelection()
      $toggleLink({
        url,
        target: "_blank",
        rel: "noopener noreferrer"
      })
    })
    this._closeLinkPanel()
  }

  removeLink(event) {
    event?.preventDefault?.()
    if (!this._linkSelection) this._captureLinkSelection()

    this.editor.update(() => {
      this._restoreLinkSelection()
      $toggleLink(null)
    })
    this._closeLinkPanel()
  }

  cancelLink(event) {
    event?.preventDefault?.()
    this._closeLinkPanel()
  }

  linkKeydown(event) {
    if (event.key === "Enter") {
      event.preventDefault()
      this.applyLink(event)
      return
    }

    if (event.key === "Escape") {
      event.preventDefault()
      this.cancelLink(event)
    }
  }

  // ─── Internals ────────────────────────────────────────────────────

  _registerOutputSync() {
    return this.editor.registerUpdateListener(({ editorState }) => {
      editorState.read(() => {
        const html = $generateHtmlFromNodes(this.editor, null)
        const inputEl = this._inputEl()
        if (inputEl && inputEl.value !== html) {
          inputEl.value = html
          inputEl.dispatchEvent(new Event("input", { bubbles: true }))
        }
      })
    })
  }

  _registerSelectionTracker() {
    this._activeFormats = { bold: false, italic: false, underline: false, list: null, link: false, linkUrl: "" }
    return this.editor.registerUpdateListener(({ editorState }) => {
      editorState.read(() => {
        const sel = $getSelection()
        if (!$isRangeSelection(sel)) {
          this._setActive({ bold: false, italic: false, underline: false, list: null, link: false, linkUrl: "" })
          return
        }
        const anchorNode = sel.anchor.getNode()
        const elementNode = anchorNode.getKey() === "root" ? anchorNode : anchorNode.getTopLevelElementOrThrow()

        let list = null
        if (elementNode.getType() === "list") {
          list = elementNode.getListType() === "bullet" ? "bullet" : "number"
        }

        let link = false
        let linkUrl = ""
        let parent = anchorNode
        while (parent) {
          if (parent.getType?.() === "link") {
            link = true
            linkUrl = parent.getURL?.() || ""
            break
          }
          parent = parent.getParent?.()
        }

        this._setActive({
          bold: sel.hasFormat("bold"),
          italic: sel.hasFormat("italic"),
          underline: sel.hasFormat("underline"),
          list: list,
          link: link,
          linkUrl: linkUrl
        })
      })
    })
  }

  _setActive(state) {
    this._activeFormats = state
    if (this.hasBtnBoldTarget)      this.btnBoldTarget.classList.toggle("btn-active", state.bold)
    if (this.hasBtnItalicTarget)    this.btnItalicTarget.classList.toggle("btn-active", state.italic)
    if (this.hasBtnUnderlineTarget) this.btnUnderlineTarget.classList.toggle("btn-active", state.underline)
    if (this.hasBtnUlTarget)        this.btnUlTarget.classList.toggle("btn-active", state.list === "bullet")
    if (this.hasBtnOlTarget)        this.btnOlTarget.classList.toggle("btn-active", state.list === "number")
    if (this.hasBtnUnlinkTarget) {
      this.btnUnlinkTarget.disabled = !state.link
      this.btnUnlinkTarget.classList.toggle("text-error", state.link)
    }
    this._syncLinkButtonState()
  }

  _captureLinkSelection() {
    if (!this.editor) return

    this.editor.getEditorState().read(() => {
      const selection = $getSelection()
      this._linkSelection = $isRangeSelection(selection) ? selection.clone() : null
    })
  }

  _restoreLinkSelection() {
    if (this._linkSelection) {
      $setSelection(this._linkSelection.clone())
    }
  }

  _normalizeLinkUrl(raw) {
    const value = raw.trim()
    if (!value) {
      return { error: this.element.dataset.linkEmpty || "Informe uma URL para o link." }
    }

    const candidate = /^[a-z][a-z0-9+.-]*:/i.test(value) ? value : `https://${value}`

    try {
      const url = new URL(candidate)
      if (url.protocol === "http:") url.protocol = "https:"
      if (url.protocol !== "https:") throw new TypeError("Invalid protocol")
      return { url: url.toString() }
    } catch (_) {
      return { error: this.element.dataset.linkInvalid || "Use an https:// URL." }
    }
  }

  _showLinkError(message) {
    if (!this.hasLinkErrorTarget) return

    this.linkErrorTarget.textContent = message
    this.linkErrorTarget.hidden = false
    if (this.hasLinkInputTarget) this.linkInputTarget.setAttribute("aria-invalid", "true")
  }

  _clearLinkError() {
    if (this.hasLinkErrorTarget) {
      this.linkErrorTarget.textContent = ""
      this.linkErrorTarget.hidden = true
    }
    if (this.hasLinkInputTarget) this.linkInputTarget.removeAttribute("aria-invalid")
  }

  _closeLinkPanel({ focusEditor = true } = {}) {
    if (this.hasLinkPanelTarget) this.linkPanelTarget.hidden = true
    this._clearLinkError()
    this._linkSelection = null
    this._syncLinkButtonState()

    if (focusEditor && this.hasSurfaceTarget) {
      this.surfaceTarget.focus()
    }
  }

  _syncLinkButtonState() {
    if (!this.hasBtnLinkTarget) return

    const panelOpen = this.hasLinkPanelTarget && !this.linkPanelTarget.hidden
    this.btnLinkTarget.classList.toggle("btn-active", Boolean(this._activeFormats?.link || panelOpen))
  }

  _inputEl() {
    if (this.hasInputTarget) return this.inputTarget
    return document.getElementById(this._inputId())
  }

  _inputId() {
    return this.hasInputIdValue ? this.inputIdValue : (this.hasInputTarget ? this.inputTarget.id : null)
  }
}
